import { useState, useEffect } from 'react';
import { Download, FileText, AlertCircle, CheckCircle, Loader2, GraduationCap, Award, Info } from 'lucide-react';
import { saveAs } from 'file-saver';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { LivretGenerationError } from '../../lib/livretGenerator';
import { generateLivretPdf } from '../../lib/livretPdfGenerator';

type StudentInfo = {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  class_name: string | null;
  numero_candidat: string | null;
  session_examen: number | null;
  date_naissance: string | null;
  lieu_naissance: string | null;
  ine: string | null;
  langue_vivante_1: string | null;
  sexe: 'M' | 'F' | 'X' | null;
};

type SubjectAggregate = {
  subject_id: string;
  subject_name: string;
  coefficient: number;
  teaching_type: string | null;
  y1_avg: number | null;
  y2_avg: number | null;
  count: number;
};

type ConseilInfo = {
  avis: 'tres_favorable' | 'favorable' | 'reserve' | 'defavorable' | null;
  observations: string | null;
  pix_certifie: boolean;
};

const AVIS_LABELS: Record<string, string> = {
  tres_favorable: 'Très favorable',
  favorable: 'Favorable',
  reserve: 'Réservé',
  defavorable: 'Défavorable',
};

const StudentLivret = () => {
  const { user } = useAuth();
  const [info, setInfo] = useState<StudentInfo | null>(null);
  const [subjects, setSubjects] = useState<SubjectAggregate[]>([]);
  const [conseil, setConseil] = useState<ConseilInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  useEffect(() => {
    if (!user?.email) return;
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const { data: studentData, error: studentErr } = await supabase
          .from('students')
          .select('*, class:classes(name)')
          .eq('email', user.email)
          .maybeSingle();
        if (studentErr) throw studentErr;
        if (!studentData) {
          setError("Aucun étudiant n'est associé à votre compte.");
          return;
        }

        const s = studentData as StudentInfo & { class?: { name: string } | null };
        const loaded: StudentInfo = {
          id: s.id,
          first_name: s.first_name,
          last_name: s.last_name,
          email: s.email,
          class_name: s.class?.name ?? null,
          numero_candidat: s.numero_candidat,
          session_examen: s.session_examen,
          date_naissance: s.date_naissance,
          lieu_naissance: s.lieu_naissance,
          ine: s.ine,
          langue_vivante_1: s.langue_vivante_1,
          sexe: s.sexe,
        };
        setInfo(loaded);

        // Évaluer les champs manquants (cohérence avec checkCompleteness côté admin)
        const missing: string[] = [];
        if (!loaded.numero_candidat) missing.push('Numéro candidat (admin)');
        if (!loaded.session_examen) missing.push("Session d'examen (admin)");
        if (!loaded.date_naissance) missing.push('Date de naissance');
        if (!loaded.lieu_naissance) missing.push('Lieu de naissance');
        if (!loaded.ine) missing.push('INE');
        if (!loaded.langue_vivante_1) missing.push('Langue vivante 1 (admin)');
        setMissingFields(missing);

        // Charger les notes pour l'aperçu
        const { data: gradesData } = await supabase
          .from('grades')
          .select('grade, is_absent, exam_date, subject:subjects(id, name, coefficient, teaching_type)')
          .eq('student_id', s.id);

        const sessionYear = loaded.session_examen ?? new Date().getFullYear();
        const y1End = `${sessionYear - 1}-08-31`;
        const acc = new Map<string, SubjectAggregate>();
        for (const g of gradesData ?? []) {
          const sub = (g as any).subject;
          if (!sub || g.is_absent || g.grade === null) continue;
          const id = sub.id as string;
          const cur = acc.get(id) ?? {
            subject_id: id,
            subject_name: sub.name as string,
            coefficient: sub.coefficient as number,
            teaching_type: sub.teaching_type as string | null,
            y1_avg: null,
            y2_avg: null,
            count: 0,
          };
          cur.count += 1;
          const examDate = g.exam_date as string | null;
          const isY1 = examDate && examDate <= y1End;
          if (isY1) {
            const prev = cur.y1_avg ?? 0;
            const n = acc.get(id)?.count ?? 0;
            cur.y1_avg = prev === 0 ? Number(g.grade) : (prev * n + Number(g.grade)) / (n + 1);
          } else {
            const prev = cur.y2_avg ?? 0;
            const n = acc.get(id)?.count ?? 0;
            cur.y2_avg = prev === 0 ? Number(g.grade) : (prev * n + Number(g.grade)) / (n + 1);
          }
          acc.set(id, cur);
        }

        // Re-compute averages properly (weighted, simple mean per year)
        const bySubjectGrades = new Map<string, { y1: number[]; y2: number[]; sub: any }>();
        for (const g of gradesData ?? []) {
          const sub = (g as any).subject;
          if (!sub || g.is_absent || g.grade === null) continue;
          const id = sub.id as string;
          if (!bySubjectGrades.has(id)) bySubjectGrades.set(id, { y1: [], y2: [], sub });
          const bucket = bySubjectGrades.get(id)!;
          const isY1 = g.exam_date && (g.exam_date as string) <= y1End;
          (isY1 ? bucket.y1 : bucket.y2).push(Number(g.grade));
        }
        const list: SubjectAggregate[] = [];
        for (const [id, v] of bySubjectGrades) {
          const avg = (arr: number[]) =>
            arr.length === 0 ? null : Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 100) / 100;
          list.push({
            subject_id: id,
            subject_name: v.sub.name,
            coefficient: v.sub.coefficient,
            teaching_type: v.sub.teaching_type,
            y1_avg: avg(v.y1),
            y2_avg: avg(v.y2),
            count: v.y1.length + v.y2.length,
          });
        }
        list.sort((a, b) => a.subject_name.localeCompare(b.subject_name));
        setSubjects(list);

        // Charger le conseil de classe
        if (loaded.session_examen) {
          const { data: conseilData } = await supabase
            .from('livret_conseil_classe')
            .select('avis, observations, pix_certifie')
            .eq('student_id', s.id)
            .eq('session_year', loaded.session_examen)
            .maybeSingle();
          if (conseilData) setConseil(conseilData as ConseilInfo);
        }
      } catch (e) {
        console.error('Error loading livret preview:', e);
        setError("Une erreur est survenue lors du chargement de l'aperçu.");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user?.email]);

  const handleDownload = async () => {
    if (!info) return;
    try {
      setIsGenerating(true);
      setError(null);
      const { blob, filename } = await generateLivretPdf(info.id);
      saveAs(blob, filename);
    } catch (e) {
      if (e instanceof LivretGenerationError) {
        const d = e.detail;
        switch (d.code) {
          case 'missing_numero_candidat':
            setError(
              "Votre numéro de candidat n'a pas encore été attribué par l'administration. Contactez votre école."
            );
            break;
          case 'missing_session_examen':
            setError("Votre session d'examen n'a pas encore été configurée par l'administration.");
            break;
          case 'config_not_found':
            setError(
              `Aucune configuration de livret n'est disponible pour ${d.bts} session ${d.session}. Contactez l'administration.`
            );
            break;
          case 'template_download_failed':
            setError(`Impossible de télécharger le template : ${d.reason}`);
            break;
          case 'template_parse_failed':
            setError(`Impossible de lire le template : ${d.reason}`);
            break;
          default:
            setError('Une erreur est survenue lors de la génération du livret.');
        }
      } else {
        setError((e as Error).message || 'Erreur inconnue');
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const canDownload = info && info.numero_candidat && info.session_examen && !isGenerating;
  const completenessPercent = info
    ? Math.round(((8 - missingFields.length) / 8) * 100)
    : 0;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertCircle className="h-5 w-5 text-error-600 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-error-700">{error ?? "Informations étudiant introuvables."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <header className="mb-8">
        <div className="flex items-center space-x-3 mb-2">
          <GraduationCap className="h-8 w-8 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">Mon livret scolaire</h1>
        </div>
        <p className="text-gray-600">
          Aperçu synthétique de votre livret officiel pour la session{' '}
          <strong>{info.session_examen ?? '—'}</strong>
        </p>
      </header>

      {/* Statut complétude */}
      <div className="mb-6 bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-gray-900">Statut du dossier</h2>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              completenessPercent === 100
                ? 'bg-success-100 text-success-700'
                : completenessPercent >= 75
                ? 'bg-warning-100 text-warning-700'
                : 'bg-error-100 text-error-700'
            }`}
          >
            {completenessPercent}% complet
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div
            className={`h-2 rounded-full transition-all ${
              completenessPercent === 100
                ? 'bg-success-500'
                : completenessPercent >= 75
                ? 'bg-warning-500'
                : 'bg-error-500'
            }`}
            style={{ width: `${completenessPercent}%` }}
          />
        </div>
        {missingFields.length > 0 ? (
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-1">Informations manquantes :</p>
            <ul className="list-disc list-inside space-y-0.5">
              {missingFields.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
            <p className="mt-2 text-xs text-gray-500 flex items-start">
              <Info className="h-4 w-4 inline mr-1 mt-0.5 flex-shrink-0" />
              <span>
                Rendez-vous sur <a href="/student/profile" className="text-primary-600 underline">Mon profil</a>{' '}
                pour compléter les champs sous votre responsabilité (DDN, INE, lieu, sexe). Les champs marqués
                "admin" sont à demander à votre école.
              </span>
            </p>
          </div>
        ) : (
          <p className="text-success-700 text-sm flex items-center">
            <CheckCircle className="h-4 w-4 mr-1" />
            Toutes les informations sont renseignées.
          </p>
        )}
      </div>

      {/* Download CTA */}
      <div className="mb-6 bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg p-6 text-white shadow-lg">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <FileText className="h-8 w-8 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-bold">Télécharger mon livret scolaire</h2>
              <p className="opacity-90 mt-1">
                Document PDF — récapitulatif de votre parcours, notes, appréciations et avis du
                conseil de classe.
              </p>
            </div>
          </div>
          <button
            onClick={handleDownload}
            disabled={!canDownload}
            className="ml-4 flex items-center space-x-2 bg-white text-primary-700 font-semibold px-5 py-2.5 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
          >
            {isGenerating ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Download className="h-5 w-5" />
            )}
            <span>{isGenerating ? 'Génération...' : 'Télécharger'}</span>
          </button>
        </div>
        {error && (
          <div className="mt-4 bg-white bg-opacity-20 rounded p-3 flex items-start text-sm">
            <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      {/* Avis conseil de classe */}
      {conseil && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <div className="flex items-center space-x-2 mb-3">
            <Award className="h-5 w-5 text-primary-600" />
            <h2 className="font-semibold text-gray-900">Avis du conseil de classe</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Avis</p>
              <p className="font-medium text-gray-900">
                {conseil.avis ? AVIS_LABELS[conseil.avis] : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Certification PIX</p>
              <p className="font-medium text-gray-900">{conseil.pix_certifie ? 'Obtenue' : 'Non certifiée'}</p>
            </div>
          </div>
          {conseil.observations && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Observations</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">{conseil.observations}</p>
            </div>
          )}
        </div>
      )}

      {/* Moyennes par matière */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Moyennes par matière</h2>
          <p className="text-sm text-gray-500 mt-1">Aperçu — détail trimestriel dans le fichier téléchargé.</p>
        </div>
        {subjects.length === 0 ? (
          <div className="p-6 text-center text-gray-500">Aucune note enregistrée.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Matière
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coef.
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    1ère année
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    2ème année
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {subjects.map((s) => (
                  <tr key={s.subject_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{s.subject_name}</div>
                      {s.teaching_type && (
                        <div className="text-xs text-gray-500">
                          {s.teaching_type === 'enseignement_professionnel' ? 'Professionnel' : 'Général'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">{s.coefficient}</td>
                    <td className="px-6 py-4 text-center text-sm">
                      {s.y1_avg !== null ? (
                        <span className={s.y1_avg >= 10 ? 'text-success-600 font-medium' : 'text-error-600 font-medium'}>
                          {s.y1_avg.toFixed(2)}/20
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      {s.y2_avg !== null ? (
                        <span className={s.y2_avg >= 10 ? 'text-success-600 font-medium' : 'text-error-600 font-medium'}>
                          {s.y2_avg.toFixed(2)}/20
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentLivret;
